import { useCallback, useState } from 'react';

import produce from 'immer';

import { deleteRequest, post, put } from 'helpers/api';
import { findEmployerRole } from 'bundles/backoffice/contacts/contact_role_employment';

import type { Contact, ContactRole, Visibility } from 'bundles/backoffice/contacts/types';

type KeysByType = { [key: string]: string };
interface ParentsByType {
  commissions?: { contact: Contact }[];
  contactRoles?: ContactRole[];
  deposits?: { payer: Contact }[];
  payables?: { payee: Contact }[];
}

// When you search for a contact to add to a record, the search results are always root contacts.
// If the record you're trying to add it to manages snapshots though, it is possible it already has
// a reference to a snapshot of that root contact. As such records shouldn't mix root contacts with
// snapshots (or snapshots with other snapshots), instead of using search results directly, this
// function should be used to find the appropriate contact to use.
export function findLocalContact(
  contact: Contact,
  parentsByType: ParentsByType,
  keysByType: KeysByType = {},
) {
  if (!contact) {
    return contact;
  }

  let snapshot: Contact | undefined;

  forEachContact(parentsByType, keysByType, (otherContact) => {
    if (contact.id) {
      if (otherContact.rootContactId === contact.id) {
        snapshot = otherContact;
      } else if (otherContact.business && otherContact.business.rootContactId === contact.id) {
        snapshot = otherContact.business;
      }
    }

    if (snapshot) {
      return false; // break
    }
  });

  return snapshot || contact;
}

// When a contact is updated in the context of a record that manages snapshots, the contact may
// actually be replaced with a new copy rather than being updated directly. Use this function after
// saving to ensure all the record's references are updated appropriately.
export function replaceContact(
  contact: Contact,
  idChanges: { [key: number]: number },
  parentsByType: ParentsByType,
  keysByType: KeysByType = {},
): ParentsByType {
  return produce(parentsByType, (draftParentsByType) => {
    forEachContact(draftParentsByType, keysByType, (otherContact, parent, contactKey) => {
      changeId(otherContact, idChanges);
      changeId(otherContact.business, idChanges);

      if (otherContact.id === contact.id) {
        parent[contactKey] = contact;
      }

      if (otherContact.business && otherContact.business.id === contact.id) {
        otherContact.business = contact;
        otherContact.businessName = contact.name;
      }
    });
  });
}

function forEachContact(
  parentsByType: ParentsByType,
  keysByType: KeysByType,
  callback: (contact: Contact, parent: object, contactKey: string) => boolean | void
) {
  for (const type in parentsByType) {
    const key = keysByType[type] || 'contact';

    for (const parent of parentsByType[type]) {
      const contact = parent[key];
      if (isContact(contact)) {
        if (callback(contact, parent, key) === false) {
          return;
        }
      }
    }
  }
}

function isContact(entity: { contactType?: string }) {
  // These functions may be used with things like Backoffice::Payable, which has a polymorphic payer
  // So, need to verify that things really are contacts
  return entity && entity.contactType;
}

function changeId(contact?: Contact, idChanges?: { [key: number]: number }) {
  if (contact && idChanges?.hasOwnProperty(contact.id)) {
    contact.id = idChanges[contact.id];
  }
}

interface Record {
  id: number;
  type: string ;
}

interface CreateContactRoleArgs {
  contact: Contact;
  employer?: Contact | null;
  recordId?: number,
  recordType?: string,
  role: string;
  sharedAttributes?: string[];
  visibility?: Visibility;
}

interface CreateContactRoleBody {
  contactId: number;
  employerRecordId?: number;
  recordId: number;
  recordType: string;
  role: string;
  sharedAttributes?: string[] | null;
  visibility?: Visibility;
}

interface UseSnapshotContactRolesOptions {
  visibility?: Visibility;
}

export function useSnapshotContactRoles(
  initialContactRoles: ContactRole[],
  record: Record,
  options: UseSnapshotContactRolesOptions = {}
) {
  const [contactRoles, setContactRoles] = useState(initialContactRoles);

  const createContactRole = useCallback(async (
    { contact, employer, recordId, recordType, role, sharedAttributes, visibility }: CreateContactRoleArgs
  ) => {
    const body: CreateContactRoleBody = {
      contactId: findLocalContact(contact, { contactRoles }).id,
      recordId: recordId || record.id,
      recordType: recordType || record.type,
      role,
      sharedAttributes,
      visibility: visibility || options.visibility,
    };

    let newContactRoles: ContactRole[] = [];
    if (employer) {
      const localEmployer = findLocalContact(employer, { contactRoles });
      employer = localEmployer || employer;

      let employerRecord = findEmployerRole(employer, role, contactRoles);
      if (!employerRecord) {
        employerRecord = await post('/crm/contacts_records', {
          ...body,
          contactId: employer.id,
          sharedAttributes: employer.sharedAttributes,
        }) as ContactRole;

        newContactRoles.push(employerRecord);
      }

      body.employerRecordId = employerRecord.id;
    }

    const contactRole = await post('/crm/contacts_records', body);
    newContactRoles.unshift(contactRole);

    setContactRoles(contactRoles => [...contactRoles, ...newContactRoles]);
  }, [contactRoles]);

  const deleteContactRole = useCallback(async (contactRole: ContactRole) => {
    await deleteRequest(contactRole.path);
    setContactRoles(contactRoles => contactRoles.filter(role => role.id !== contactRole.id));
  }, []);

  const updateContactRole = useCallback(async (contactRole: ContactRole) => {
    await put(contactRole.path, { ...contactRole, contactId: contactRole.contact.id });
    setContactRoles(contactRoles => contactRoles.map(role => role.id === contactRole.id ? contactRole : role));
  }, []);

  const _replaceContact = useCallback((contact: Contact, idChanges: { [key: number]: number }) => {
    setContactRoles((contactRoles) => {
      return replaceContact(contact, idChanges, { contactRoles }).contactRoles as ContactRole[];
    });
  }, []);

  return { contactRoles, createContactRole, deleteContactRole, updateContactRole, replaceContact: _replaceContact };
}
